import {
  type IconConfig,
  leaveProject as leaveProjectRequest,
  type Project,
  getSDK,
  useStores,
} from '@littlebird/sdk';
import { mapShareError } from '@littlebird/ui-utils';
import { useCallback } from 'react';
import { toast } from 'sonner';

import { trackEvent } from '@/utils/analytics';

import { projectSuggestionEventProps } from './project-suggestion-analytics';

export type ProjectAssignmentTarget =
  | { kind: 'thread'; id: string }
  | { kind: 'meeting'; id: string };

export interface CreateProjectOptions {
  waitForServer?: boolean;
  projectIcon?: IconConfig | null;
}

const PROJECT_SUGGESTION_DISMISSAL_STREAK_KEY =
  'project-suggestion-dismissal-streak';

export function useProjectActions() {
  const { meetings, projects, threads } = useStores();

  const reportActionError = useCallback(
    (message: string, toastMessage: string, error: unknown) => {
      toast.error(toastMessage);
      console.error(message, error);
    },
    []
  );

  const createProject = useCallback(
    async (
      name: string,
      options: CreateProjectOptions = {}
    ): Promise<Project | null> => {
      try {
        const project = await projects.createProject(name, {
          waitForServerAck: options.waitForServer,
          icon: options.projectIcon ?? null,
        });
        trackEvent('PROJECT_CREATED');
        return project;
      } catch (error) {
        reportActionError(
          'Failed to create project',
          'Failed to create project. Please try again.',
          error
        );
        return null;
      }
    },
    [projects, reportActionError]
  );

  const renameProject = useCallback(
    async (projectId: string, name: string): Promise<Project | null> => {
      try {
        return await projects.renameProject(projectId, name);
      } catch (error) {
        reportActionError(
          'Failed to rename project',
          'Failed to rename project. Please try again.',
          error
        );
        return null;
      }
    },
    [projects, reportActionError]
  );

  const setProjectIcon = useCallback(
    async (project: Project, projectIcon: IconConfig | null): Promise<void> => {
      try {
        await projects.updateProject(project, { icon: projectIcon });
        trackEvent('PROJECT_ICON_CHANGED', {
          has_icon: projectIcon?.code != null,
        });
      } catch (error) {
        reportActionError(
          'Failed to update project icon',
          'Failed to update project icon. Please try again.',
          error
        );
      }
    },
    [projects, reportActionError]
  );

  const deleteProject = useCallback(
    async (projectId: string): Promise<boolean> => {
      try {
        await projects.deleteProject(projectId);
        trackEvent('PROJECT_DELETED');
        return true;
      } catch (error) {
        reportActionError(
          'Failed to delete project',
          'Failed to delete project. Please try again.',
          error
        );
        return false;
      }
    },
    [projects, reportActionError]
  );

  const leaveProject = useCallback(
    async (projectId: string): Promise<boolean> => {
      try {
        await leaveProjectRequest(getSDK(), projectId);
        trackEvent('PROJECT_LEFT');
        void projects.refreshProjects();
        return true;
      } catch (error) {
        reportActionError(
          'Failed to leave project',
          mapShareError(error),
          error
        );
        return false;
      }
    },
    [projects, reportActionError]
  );

  const setProjectAssignment = useCallback(
    async (
      target: ProjectAssignmentTarget,
      projectId: string | null
    ): Promise<boolean> => {
      const assignToProject =
        target.kind === 'thread'
          ? threads.setProjectId(target.id, projectId)
          : meetings.setProjectId(target.id, projectId);

      try {
        await assignToProject;
        if (projectId) {
          trackEvent('ASSIGNED_TO_PROJECT', { item_type: target.kind });
        } else {
          trackEvent('REMOVED_FROM_PROJECT', { item_type: target.kind });
        }
        return true;
      } catch (error) {
        reportActionError(
          'Failed to update project assignment',
          projectId
            ? 'Failed to move to project. Please try again.'
            : 'Failed to remove from project. Please try again.',
          error
        );
        return false;
      }
    },
    [meetings, reportActionError, threads]
  );

  const dismissProjectSuggestion = useCallback(
    async (threadId: string): Promise<void> => {
      const suggestion = threads.getById(threadId)?.suggestedProject;
      if (!suggestion) return;
      try {
        await threads.clearProjectSuggestion(threadId);
        const streak =
          (Number(
            localStorage.getItem(PROJECT_SUGGESTION_DISMISSAL_STREAK_KEY)
          ) || 0) + 1;
        if (streak < 3) {
          localStorage.setItem(
            PROJECT_SUGGESTION_DISMISSAL_STREAK_KEY,
            String(streak)
          );
        } else {
          localStorage.removeItem(PROJECT_SUGGESTION_DISMISSAL_STREAK_KEY);
          getSDK().userConfig.chat.set('project_suggestions_enabled', false);
        }
        trackEvent(
          'PROJECT_SUGGESTION_DISMISSED',
          projectSuggestionEventProps(suggestion)
        );
      } catch (error) {
        reportActionError(
          'Failed to dismiss project suggestion',
          'Failed to dismiss suggestion. Please try again.',
          error
        );
      }
    },
    [threads, reportActionError]
  );

  const acceptProjectSuggestion = useCallback(
    async (threadId: string): Promise<void> => {
      const thread = threads.getById(threadId);
      const suggestion = thread?.suggestedProject;
      if (!thread || !suggestion) return;

      try {
        let projectId = suggestion.projectId;
        let createdProjectId: string | null = null;
        if (!projectId || !projects.getById(projectId)) {
          const created = await createProject(suggestion.name, {
            waitForServer: true,
          });
          if (!created) return;
          projectId = created.id;
          createdProjectId = created.id;
        }

        const assigned = await setProjectAssignment(
          { kind: 'thread', id: threadId },
          projectId
        );
        if (!assigned) {
          if (createdProjectId) {
            try {
              await projects.deleteProject(createdProjectId);
            } catch (error) {
              console.error(
                'Failed to roll back project after assignment failure',
                error
              );
            }
          }
          return;
        }

        trackEvent(
          'PROJECT_SUGGESTION_ACCEPTED',
          projectSuggestionEventProps(suggestion)
        );
        localStorage.removeItem(PROJECT_SUGGESTION_DISMISSAL_STREAK_KEY);
      } catch (error) {
        reportActionError(
          'Failed to accept project suggestion',
          'Failed to accept suggestion. Please try again.',
          error
        );
      }
    },
    [createProject, projects, reportActionError, setProjectAssignment, threads]
  );

  return {
    createProject,
    renameProject,
    setProjectIcon,
    deleteProject,
    leaveProject,
    setProjectAssignment,
    acceptProjectSuggestion,
    dismissProjectSuggestion,
  };
}
